import { useEffect, useState, useMemo, useCallback } from "react";
import { api, sendResendEmail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  HardDrive,
  Clock,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Sparkles,
  RotateCcw,
  Sliders,
  FolderCheck,
  Send,
  Video,
} from "lucide-react";
import { toast } from "sonner";

const HOURS_LIST = [
  "00:00", "01:00", "02:00", "03:00", "04:00", "05:00", "06:00", "07:00",
  "08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00",
  "16:00", "17:00", "18:00", "19:00", "20:00", "21:00", "22:00", "23:00"
];

export default function Slots() {
  // y = Güncel Mevcut Video Sayısı (Cloud R2 Konsolundaki Gerçek 285 Nesne)
  const [currentVideoCount, setCurrentVideoCount] = useState(() => {
    const saved = localStorage.getItem("1011_live_r2_count");
    return saved !== null && Number(saved) > 0 ? Number(saved) : 285;
  });
  const [loading, setLoading] = useState(false);

  // x = Saat başı yüklenmesi gereken video sayısı (Manuel Giriş)
  const [targetHourlyVideos, setTargetHourlyVideos] = useState(() => {
    const saved = localStorage.getItem("1011_target_hourly_videos");
    return saved !== null ? Number(saved) : 10;
  });

  // y0 = Başlangıç Video Sayısı (Referans - Her Sabah 10:00 Otomatik)
  const [baseVideoCount, setBaseVideoCount] = useState(() => {
    const saved = localStorage.getItem("1011_base_video_count");
    return saved !== null && Number(saved) > 0 ? Number(saved) : 285;
  });

  // Sabah 10:00 referans tarihi takibi
  const [lastRefDate, setLastRefDate] = useState(() => {
    return localStorage.getItem("1011_base_video_count_date") || "";
  });

  // Otomatik yenileme ve mail gönderim log takipleri
  const [lastAutoRefreshTime, setLastAutoRefreshTime] = useState(() => {
    return localStorage.getItem("1011_last_auto_refresh_time") || "";
  });
  const [lastAutoMailTime, setLastAutoMailTime] = useState(() => {
    return localStorage.getItem("1011_last_auto_mail_time") || "";
  });

  // Manuel Saat Girişi
  const [startHourStr, setStartHourStr] = useState(() => {
    return localStorage.getItem("1011_range_start_hour") || "18:00";
  });
  const [endHourStr, setEndHourStr] = useState(() => {
    return localStorage.getItem("1011_range_end_hour") || "01:00";
  });

  // Bozuk / Sorunlu Video Bildirim State
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueForm, setIssueForm] = useState({ videoName: "", facility: "", issueType: "görüntü", details: "" });
  const [sendingIssueMail, setSendingIssueMail] = useState(false);
  const [sendingWarningMail, setSendingWarningMail] = useState(false);

  // Cloudflare R2 Canlı Video Sayısını Otomatik Çek (10-11 Bucket / videos Klasörü)
  const fetchR2Count = useCallback(async (showToast = false) => {
    setLoading(true);
    try {
      const res = await api.get("/r2/videos");
      if (res.data && res.data.total_count !== undefined) {
        const liveCount = res.data.total_count;
        setCurrentVideoCount(liveCount);
        localStorage.setItem("1011_live_r2_count", String(liveCount));

        if (showToast) {
          toast.success(`Cloudflare R2 canlı video sayısı güncellendi: ${liveCount} Nesne / Video`);
        }
        return liveCount;
      }
    } catch (e) {
      if (showToast) toast.error("Cloud R2 verisi alınamadı.");
    } finally {
      setLoading(false);
    }
    return null;
  }, []);

  // Otomatik Mail Uyarısı İletici Fonksiyonu
  const triggerAutoWarningMail = useCallback(async (hourStr, expected, current) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const alertKey = `1011_alert_sent_${todayStr}_${hourStr}`;
    if (localStorage.getItem(alertKey)) return; // Bu saat için zaten gönderildi

    try {
      const config = JSON.parse(localStorage.getItem("1011_email_automation_config") || "{}");
      const recipients = config.videoWarningEmails || "admin@10-11.com.tr";

      const res = await sendResendEmail({
        to: recipients,
        subject: `🚨 ACİL OTOMATİK UYARI (${hourStr}): Video Kayıt Sayısı Tutarsız!`,
        html: `
          <div style="font-family:Arial,sans-serif;padding:24px;background:#0f172a;color:#f8fafc;border-radius:12px;border:1px solid #f43f5e;">
            <h2 style="color:#fb7185;margin-top:0;">🔴 10-11 Otomatik Saatlik Video Tutarsızlık Uyarısı</h2>
            <p style="font-size:14px;color:#cbd5e1;">Saat <b>${hourStr}</b> itibarıyla yapılan otomatik kontrolde (dakika 21) kayıt sayısı hedefin altında kalmıştır:</p>
            <table style="width:100%;border-collapse:collapse;color:#cbd5e1;font-size:14px;margin:16px 0;">
              <tr style="background:#1e293b;"><td style="padding:10px;border:1px solid #334155;"><strong>Kontrol Saati:</strong></td><td style="padding:10px;border:1px solid #334155;color:#f8fafc;font-weight:bold;">${hourStr} (xx:21 Otomatik Kontrol)</td></tr>
              <tr style="background:#0f172a;"><td style="padding:10px;border:1px solid #334155;"><strong>Beklenen Video Sayısı:</strong></td><td style="padding:10px;border:1px solid #334155;color:#38bdf8;font-weight:bold;">${expected} Video</td></tr>
              <tr style="background:#1e293b;"><td style="padding:10px;border:1px solid #334155;"><strong>Mevcut R2 Sayısı:</strong></td><td style="padding:10px;border:1px solid #334155;color:#f87171;font-weight:bold;">${current} Video</td></tr>
              <tr style="background:#0f172a;"><td style="padding:10px;border:1px solid #334155;"><strong>Eksik Miktar:</strong></td><td style="padding:10px;border:1px solid #334155;color:#f43f5e;font-weight:bold;">${Math.max(0, expected - current)} Video Eksik</td></tr>
            </table>
            <hr style="border:0;border-top:1px solid #334155;margin:16px 0;" />
            <p style="font-size:11px;color:#94a3b8;">10-11 Otomatik Slot & Video Takip Sistemi (Otomatik UYARI - xx:21)</p>
          </div>
        `
      });

      if (res && res.success) {
        localStorage.setItem(alertKey, "true");
        const timeStr = new Date().toLocaleTimeString("tr-TR");
        setLastAutoMailTime(`${todayStr} ${timeStr} (${hourStr})`);
        localStorage.setItem("1011_last_auto_mail_time", `${todayStr} ${timeStr} (${hourStr})`);
        toast.warning(`🚨 Otomatik UYARI Maili İletildi! (${hourStr} - Eksik Video)`);
      }
    } catch (e) {
      console.error("Auto mail error:", e);
    }
  }, []);

  // Sayfa Açıldığında ve Zamanlayıcılar (10:00 Referansı, xx:20 R2 Yenileme, xx:21 Mail Gönderimi)
  useEffect(() => {
    fetchR2Count(false);

    const timer = setInterval(() => {
      const now = new Date();
      const todayStr = now.toISOString().slice(0, 10);
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();
      const currentHourStr = `${String(hours).padStart(2, "0")}:00`;

      // 1) Her Sabah 10:00'da Otomatik Referans Alımı (y0)
      const storedRefDate = localStorage.getItem("1011_base_video_count_date");
      if (hours >= 10 && storedRefDate !== todayStr) {
        fetchR2Count(false).then((liveVal) => {
          const valToUse = liveVal || currentVideoCount;
          setBaseVideoCount(valToUse);
          setLastRefDate(todayStr);
          localStorage.setItem("1011_base_video_count", String(valToUse));
          localStorage.setItem("1011_base_video_count_date", todayStr);
          toast.info(`☀️ Sabah 10:00 Otomatik Referansı Alındı: ${valToUse} Video`);
        });
      }

      // 2) Her Saat xx:20'de R2 Otomatik Yenileme
      if (minutes === 20 && seconds < 5) {
        const refreshKey = `1011_auto_refreshed_${todayStr}_${hours}`;
        if (!localStorage.getItem(refreshKey)) {
          localStorage.setItem(refreshKey, "true");
          fetchR2Count(false).then((liveVal) => {
            const timeStr = `${String(hours).padStart(2, "0")}:20`;
            setLastAutoRefreshTime(`${todayStr} ${timeStr}`);
            localStorage.setItem("1011_last_auto_refresh_time", `${todayStr} ${timeStr}`);
            toast.success(`🔄 Otomatik R2 Yenilendi (${timeStr}): ${liveVal || currentVideoCount} Video`);
          });
        }
      }

      // 3) Her Saat xx:21'de Otomatik Tutarsızlık Maili Kontrolü & Gönderimi
      if (minutes === 21 && seconds < 5) {
        const startH = parseInt(startHourStr.split(":")[0], 10);
        const endH = parseInt(endHourStr.split(":")[0], 10);

        // Seçili saat aralığında mı kontrol et
        let inRange = false;
        let curr = startH;
        let guard = 0;
        let stepIndexForCurrent = -1;
        let stepCounter = 1;

        while (guard < 24) {
          const checkHourStr = `${String((curr + 1) % 24).padStart(2, "0")}:00`;
          if (checkHourStr === currentHourStr) {
            inRange = true;
            stepIndexForCurrent = stepCounter;
            break;
          }
          if (curr === endH) break;
          curr = (curr + 1) % 24;
          stepCounter++;
          guard++;
        }

        if (inRange && stepIndexForCurrent > 0) {
          const expected = baseVideoCount + stepIndexForCurrent * targetHourlyVideos;
          if (currentVideoCount < expected) {
            triggerAutoWarningMail(currentHourStr, expected, currentVideoCount);
          }
        }
      }
    }, 3000); // 3 saniyede bir zamanlayıcı kontrolü

    return () => clearInterval(timer);
  }, [fetchR2Count, baseVideoCount, targetHourlyVideos, startHourStr, endHourStr, currentVideoCount, triggerAutoWarningMail]);

  // Manuel x Değeri Değiştirildiğinde
  const handleTargetChange = (val) => {
    const num = Math.max(0, Number(val) || 0);
    setTargetHourlyVideos(num);
    localStorage.setItem("1011_target_hourly_videos", String(num));
  };

  // Saat Aralığı Değiştiğinde
  const handleStartHourChange = (val) => {
    setStartHourStr(val);
    localStorage.setItem("1011_range_start_hour", val);
  };
  const handleEndHourChange = (val) => {
    setEndHourStr(val);
    localStorage.setItem("1011_range_end_hour", val);
  };

  // Takibi Yeniden Başlat (y0 Sayısını Şu Anki Canlı Sayıya Eşitle)
  const handleResetBaseline = () => {
    setBaseVideoCount(currentVideoCount);
    localStorage.setItem("1011_base_video_count", String(currentVideoCount));
    toast.success(`Takip referansı sıfırlandı! Başlangıç (${startHourStr}): ${currentVideoCount} Video`);
  };

  // Başlangıç Saati ve Bitiş Saati Arasındaki Saatleri Üret
  const hoursInRange = useMemo(() => {
    const startH = parseInt(startHourStr.split(":")[0], 10);
    const endH = parseInt(endHourStr.split(":")[0], 10);

    const hours = [];
    let curr = startH;
    let guard = 0;

    while (guard < 24) {
      if (curr !== startH) {
        hours.push(`${String(curr).padStart(2, "0")}:00`);
      }
      if (curr === endH) break;
      curr = (curr + 1) % 24;
      guard++;
    }

    if (hours.length === 0) {
      hours.push(`${String((startH + 1) % 24).padStart(2, "0")}:00`);
    }

    return hours;
  }, [startHourStr, endHourStr]);

  // Saatlik Kontrol Listesini Hesapla (Sadece Seçili Saatler Arası)
  const hourlyStatusList = useMemo(() => {
    const x = Number(targetHourlyVideos) || 0;
    const y0 = Number(baseVideoCount) || 0;
    const currentY = Number(currentVideoCount) || 0;

    return hoursInRange.map((hourStr, index) => {
      const stepIndex = index + 1; // 1. saat, 2. saat...
      const expectedCount = y0 + stepIndex * x; // y0 + (N * x)
      const isConsistent = currentY >= expectedCount;

      return {
        stepIndex,
        hourStr,
        expectedCount,
        currentY,
        isConsistent,
      };
    });
  }, [hoursInRange, baseVideoCount, currentVideoCount, targetHourlyVideos]);

  return (
    <div className="space-y-8">
      {/* Üst Başlık */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" /> Cloudflare R2 Bucket: 10-11 / videos (Canlı {currentVideoCount} Nesne)
            </span>
          </div>
          <h1 className="font-anton text-4xl sm:text-5xl text-white tracking-wide">
            SLOT & VİDEO TAKİP PANELİ
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => fetchR2Count(true)}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold shadow-lg shadow-emerald-600/20 gap-2 h-11"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            R2 Canlı Yenile
          </Button>

          <Button
            onClick={handleResetBaseline}
            variant="outline"
            className="border-slate-800 text-slate-300 hover:bg-slate-800 h-11 gap-2"
          >
            <RotateCcw className="w-4 h-4 text-cyan-400" />
            Referansı Sıfırla
          </Button>

          {/* Bozuk Video Bildir Butonu */}
          <Dialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 h-11 gap-2">
                <AlertTriangle className="w-4 h-4" />
                Bozuk Video Bildir
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
              <DialogHeader>
                <DialogTitle className="font-anton text-2xl text-amber-400 flex items-center gap-2">
                  <Video className="w-6 h-6" /> BOZUK / SORUNLU VİDEO BİLDİRİMİ
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="space-y-2">
                  <Label>Video Adı / ID</Label>
                  <Input
                    value={issueForm.videoName}
                    onChange={(e) => setIssueForm({ ...issueForm, videoName: e.target.value })}
                    placeholder="Örn: 2026-08-09_18-00_Saha1.mp4"
                    className="bg-slate-950 border-slate-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Saha / Tesis Adı</Label>
                  <Input
                    value={issueForm.facility}
                    onChange={(e) => setIssueForm({ ...issueForm, facility: e.target.value })}
                    placeholder="Örn: Kadıköy Halı Saha"
                    className="bg-slate-950 border-slate-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sorun Tipi</Label>
                  <Select value={issueForm.issueType} onValueChange={(v) => setIssueForm({ ...issueForm, issueType: v })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-200">
                      <SelectItem value="görüntü">Görüntü Bozuk / Donuyor</SelectItem>
                      <SelectItem value="ses">Ses Yok / Cızırtılı</SelectItem>
                      <SelectItem value="eksik">Video Yarıda Kesilmiş</SelectItem>
                      <SelectItem value="açılmıyor">Video Açılmıyor / Siyah Ekran</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Detaylı Açıklama</Label>
                  <Textarea
                    value={issueForm.details}
                    onChange={(e) => setIssueForm({ ...issueForm, details: e.target.value })}
                    placeholder="Sorunun ayrıntılarını yazınız..."
                    className="bg-slate-950 border-slate-800 min-h-[80px]"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  disabled={sendingIssueMail}
                  onClick={async () => {
                    if (!issueForm.videoName.trim()) return toast.error("Lütfen video adını giriniz.");
                    setSendingIssueMail(true);
                    try {
                      const config = JSON.parse(localStorage.getItem("1011_email_automation_config") || "{}");
                      const recipients = config.issueVideoEmails || "admin@10-11.com.tr";
                      const res = await sendResendEmail({
                        to: recipients,
                        subject: `🚨 ACİL: Bozuk Video Bildirimi - ${issueForm.videoName}`,
                        html: `
                          <div style="font-family:Arial,sans-serif;padding:24px;background:#0f172a;color:#f8fafc;border-radius:12px;border:1px solid #ef4444;">
                            <h2 style="color:#f87171;margin-top:0;">⚠️ 10-11 Bozuk Video Bildirimi</h2>
                            <table style="width:100%;color:#cbd5e1;font-size:14px;">
                              <tr><td style="padding:8px 0;"><strong>Video Dosyası:</strong></td><td style="font-weight:bold;color:#f8fafc;">${issueForm.videoName}</td></tr>
                              <tr><td style="padding:8px 0;"><strong>Saha / Tesis:</strong></td><td>${issueForm.facility || "-"}</td></tr>
                              <tr><td style="padding:8px 0;"><strong>Sorun Tipi:</strong></td><td style="color:#f87171;font-weight:bold;">${issueForm.issueType.toUpperCase()}</td></tr>
                              <tr><td style="padding:8px 0;"><strong>Açıklama:</strong></td><td>${issueForm.details || "Detay girilmedi."}</td></tr>
                            </table>
                            <hr style="border:0;border-top:1px solid #334155;margin:16px 0;" />
                            <p style="font-size:11px;color:#94a3b8;">Bu e-posta 10-11 Slot & Video Takip Paneli üzerinden gönderilmiştir.</p>
                          </div>
                        `
                      });
                      if (res && res.success) {
                        toast.success("Bozuk video bildirimi alıcılara başarıyla iletildi!");
                        setIssueDialogOpen(false);
                        setIssueForm({ videoName: "", facility: "", issueType: "görüntü", details: "" });
                      } else {
                        toast.error(res?.message || "E-posta gönderilemedi.");
                      }
                    } catch (e) {
                      toast.error("E-posta gönderim hatası.");
                    } finally {
                      setSendingIssueMail(false);
                    }
                  }}
                  className="bg-amber-600 hover:bg-amber-500 text-white font-semibold"
                >
                  <Send className="w-4 h-4 mr-2" /> Bildirimi Gönder
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Sayaçlar ve Manuel Giriş Kutusları Paneli */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sayaç 1: OTOMATİK Canlı Video Sayısı (y) */}
        <Card className="glass-card p-6 rounded-3xl border border-slate-800 space-y-4 relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-cyan-400" /> Cloudflare R2 Nesne / Video Sayısı (y)
            </span>
            <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
              <FolderCheck className="w-3 h-3 text-emerald-400" /> Bucket: 10-11 (videos/)
            </span>
          </div>

          <div className="flex items-baseline gap-3">
            <span className="font-anton text-6xl text-white tracking-wide">
              {currentVideoCount}
            </span>
            <span className="text-sm text-emerald-400 font-semibold">Objects / Video (99.81 GB)</span>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-400 pt-2 border-t border-slate-800/80">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>Cloudflare R2 Bucket '10-11' canlı eşlenmiştir. Toplam {currentVideoCount} nesne aktif taranır.</span>
          </div>
        </Card>

        {/* Sayaç 2: Manuel Giriş Kutucuğu (x) */}
        <Card className="glass-card p-6 rounded-3xl border border-slate-800 space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="target-input" className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-400" /> Saat başı yüklenmesi gereken video sayısı (x)
            </Label>
            <Sparkles className="w-4 h-4 text-purple-400" />
          </div>

          <div className="space-y-2">
            <Input
              id="target-input"
              type="number"
              min="0"
              value={targetHourlyVideos}
              onChange={(e) => handleTargetChange(e.target.value)}
              className="bg-slate-950 border-slate-800 text-3xl font-anton h-16 text-purple-300 focus:ring-purple-500"
              placeholder="Örn: 10"
            />
            <p className="text-xs text-slate-400">
              Formül: <b className="text-purple-300">y₀ + (Saat × x)</b>
            </p>
          </div>
        </Card>
      </div>

      {/* Manuel Saat Giriş Yeri (Başlangıç Saati ve Bitiş Saati Seçim Kutuları) */}
      <Card className="glass-card p-6 rounded-3xl border border-slate-800 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2">
            <Sliders className="w-4 h-4 text-cyan-400" /> Kontrol Edilecek Saat Aralığı Girişi (Başlangıç & Bitiş)
          </span>
          <span className="text-xs text-slate-400">Sadece girdiğiniz saatler arası kontrol edilir</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Başlangıç Saati Seçimi */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-400">Başlangıç Saati</Label>
            <Select value={startHourStr} onValueChange={handleStartHourChange}>
              <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 h-12">
                <SelectValue placeholder="Başlangıç Saati" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-200">
                {HOURS_LIST.map((h) => (
                  <SelectItem key={h} value={h}>{h}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bitiş Saati Seçimi */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-400">Bitiş Saati</Label>
            <Select value={endHourStr} onValueChange={handleEndHourChange}>
              <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 h-12">
                <SelectValue placeholder="Bitiş Saati" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-200">
                {HOURS_LIST.map((h) => (
                  <SelectItem key={h} value={h}>{h}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Referans Bilgi Bandı */}
      <Card className="bg-slate-900/60 border border-slate-800/80 p-4 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-xs text-slate-300">
          <Clock className="w-4 h-4 text-cyan-400 shrink-0" />
          <span>
            Kontrol Aralığı: <b className="text-white">{startHourStr} - {endHourStr}</b> | Sabah 10:00 Referansı (y₀): <b className="text-emerald-400">{baseVideoCount} Video</b> | Saatlik Hedef (x): <b className="text-purple-300">{targetHourlyVideos} Video/Saat</b>
          </span>
        </div>
        <span className="text-xs text-slate-400">
          Otomatik Canlı Video (y): <b className="text-emerald-400">{currentVideoCount}</b>
        </span>
      </Card>

      {/* Otomasyon Zamanlayıcı Durum Bandı */}
      <Card className="glass-card bg-slate-950/80 border border-slate-800 p-5 rounded-2xl space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-extrabold uppercase tracking-wider text-emerald-400 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400" /> Otomatik Zamanlayıcı Akışı & Mail Uyarısı Aktif
          </span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" /> Sistem Canlı Dinleniyor
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5 font-bold text-slate-200">
              <span>☀️ Sabah 10:00 Referansı</span>
            </div>
            <p className="text-[11px] text-slate-400">
              Her sabah 10:00'da y₀ referans sayısı otomatik alınır: <b className="text-emerald-400">{baseVideoCount} Video</b>
            </p>
          </div>

          <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5 font-bold text-slate-200">
              <span>🔄 Saatlik xx:20 R2 Yenileme</span>
            </div>
            <p className="text-[11px] text-slate-400">
              {lastAutoRefreshTime ? (
                <>Son Otomatik Yenileme: <b className="text-cyan-400">{lastAutoRefreshTime}</b></>
              ) : (
                <>Seçili saatlerde her saat <b>xx:20</b>'de R2 otomatik sorgulanır.</>
              )}
            </p>
          </div>

          <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5 font-bold text-slate-200">
              <span>🚨 Saatlik xx:21 UYARI Maili</span>
            </div>
            <p className="text-[11px] text-slate-400">
              {lastAutoMailTime ? (
                <>Son Otomatik UYARI: <b className="text-rose-400">{lastAutoMailTime}</b></>
              ) : (
                <>Her saat <b>xx:21</b>'de tutarsızlık varsa tanımlı e-postalara otomatik gönderilir.</>
              )}
            </p>
          </div>
        </div>
      </Card>

      {/* Saatler Arası Tutarlılık Kontrol Listesi */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-anton text-2xl text-white tracking-wide flex items-center gap-2">
            {startHourStr} - {endHourStr} ARASI SAATLİK KONTROL SONUÇLARI
          </h2>
          {hourlyStatusList.some((item) => !item.isConsistent) && (
            <Button
              size="sm"
              disabled={sendingWarningMail}
              onClick={async () => {
                const inconsistentItems = hourlyStatusList.filter((item) => !item.isConsistent);
                setSendingWarningMail(true);
                try {
                  const config = JSON.parse(localStorage.getItem("1011_email_automation_config") || "{}");
                  const recipients = config.videoWarningEmails || "admin@10-11.com.tr";
                  const rowsHtml = inconsistentItems
                    .map((item) => `<tr><td style="padding:6px;border:1px solid #334155;">${item.hourStr}</td><td style="padding:6px;border:1px solid #334155;">${item.expectedCount}</td><td style="padding:6px;border:1px solid #334155;color:#f87171;font-weight:bold;">${item.currentY}</td></tr>`)
                    .join("");

                  const res = await sendResendEmail({
                    to: recipients,
                    subject: `⚠️ UYARI: Saatlik Video Sayısı Hedefin Altında! (${inconsistentItems.length} Saat Tutarsız)`,
                    html: `
                      <div style="font-family:Arial,sans-serif;padding:24px;background:#0f172a;color:#f8fafc;border-radius:12px;border:1px solid #f43f5e;">
                        <h2 style="color:#fb7185;margin-top:0;">🔴 10-11 Video Tutarsızlık Uyarısı</h2>
                        <p>Aşağıdaki saatlerde yüklenen video sayısı beklenen hedefin altında kalmıştır:</p>
                        <table style="width:100%;border-collapse:collapse;color:#cbd5e1;font-size:13px;margin:16px 0;">
                          <thead>
                            <tr style="background:#1e293b;color:#f8fafc;">
                              <th style="padding:8px;border:1px solid #334155;text-align:left;">Saat</th>
                              <th style="padding:8px;border:1px solid #334155;text-align:left;">Beklenen Hedef</th>
                              <th style="padding:8px;border:1px solid #334155;text-align:left;">Mevcut Video</th>
                            </tr>
                          </thead>
                          <tbody>${rowsHtml}</tbody>
                        </table>
                        <hr style="border:0;border-top:1px solid #334155;margin:16px 0;" />
                        <p style="font-size:11px;color:#94a3b8;">10-11 Slot & Video Takip Otomasyonu</p>
                      </div>
                    `
                  });
                  if (res && res.success) {
                    toast.success("Tutarsızlık uyarısı ilgili e-posta alıcılarına gönderildi!");
                  } else {
                    toast.error(res?.message || "Uyarı maili gönderilemedi.");
                  }
                } catch (e) {
                  toast.error("E-posta gönderim hatası.");
                } finally {
                  setSendingWarningMail(false);
                }
              }}
              className="bg-rose-600 hover:bg-rose-500 text-white font-semibold text-xs gap-1.5"
            >
              <Send className="w-3.5 h-3.5" /> Tutarsızlık Uyarısı Gönder ({hourlyStatusList.filter((item) => !item.isConsistent).length})
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {hourlyStatusList.map((item) => (
            <Card
              key={item.hourStr}
              className={`p-5 rounded-2xl border transition-all ${
                item.isConsistent
                  ? "bg-emerald-950/30 border-emerald-500/40 hover:border-emerald-400/60"
                  : "bg-rose-950/30 border-rose-500/40 hover:border-rose-400/60"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {item.isConsistent ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 animate-bounce" />
                    )}
                    <span className={`font-bold text-base ${item.isConsistent ? "text-emerald-300" : "text-rose-300"}`}>
                      {item.hourStr} kayıt sayısı {item.isConsistent ? "tutarlı" : "tutarsız lütfen kontrol edin"}
                    </span>
                  </div>

                  <p className="text-xs text-slate-400 pl-7">
                    Beklenen Hedef: <b>{item.expectedCount} Video</b> ({baseVideoCount} + {item.stepIndex}×{targetHourlyVideos}) | Mevcut: <b>{item.currentY} Video</b>
                  </p>
                </div>

                <span
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0 ${
                    item.isConsistent
                      ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                      : "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                  }`}
                >
                  {item.isConsistent ? "🟢 OK" : "🔴 UYARI"}
                </span>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
